import { ReactElement, useState } from 'react';

import { Badge } from 'react-bootstrap';
import Button from '@/common/Button';
import Table from '@/common/Table';
import Tippy from '@tippyjs/react';
import { TrainingChecklistTableTypes } from './types';

const STATUS_BADGE: Record<TrainingChecklistTableTypes.CategoryStatus, { label: string; bg: string }> = {
  [TrainingChecklistTableTypes.CategoryStatus.PENDING]: { label: 'Pending', bg: 'gray-subtle' },
  [TrainingChecklistTableTypes.CategoryStatus.IN_PROGRESS]: { label: 'In Progress', bg: 'primary' },
  [TrainingChecklistTableTypes.CategoryStatus.COMPLETED]: { label: 'Completed', bg: 'success' },
};

export type CategoryRowProps = {
  data: TrainingChecklistTableTypes.ChecklistItem;
  onDoubleClick: (data: TrainingChecklistTableTypes.ChecklistItem) => void;
  onEvaluate: (data: TrainingChecklistTableTypes.ChecklistItem) => Promise<void>;
};

const CategoryRow = ({ data, onDoubleClick, onEvaluate }: CategoryRowProps): ReactElement => {
  const badge = STATUS_BADGE[data.status];
  const [isStarting, setIsStarting] = useState<boolean>(false);

  const handleEvaluateClick = async (): Promise<void> => {
    if (isStarting) return;
    setIsStarting(true);
    try {
      await onEvaluate(data);
    } finally {
      setIsStarting(false);
    }
  };

  return (
    <Table.Row
      id={data.id}
      virtualize={false}
      onDoubleClick={(): void => {
        if (data.status === TrainingChecklistTableTypes.CategoryStatus.COMPLETED) onDoubleClick(data);
      }}
    >
      <Table.Cell className="CATEGORY">{data.name}</Table.Cell>
      <Table.Cell className="STATUS">
        <Badge bg={badge.bg} pill>
          {badge.label}
        </Badge>
      </Table.Cell>
      <Table.Cell className="TRAINER" data-placeholder="--">
        {data.trainerFullName || ''}
      </Table.Cell>
      <Table.Cell className="ACTION">
        {data.status === TrainingChecklistTableTypes.CategoryStatus.COMPLETED && <i className="sv sv-check fs-3 text-success fw-bold" />}
        {data.status !== TrainingChecklistTableTypes.CategoryStatus.COMPLETED && (
          <Tippy content="Your role can't evaluate this category." disabled={data.canEvaluate}>
            <div className="w-100">
              <Button variant="primary" className="w-100" disabled={isStarting || !data.canEvaluate} onClick={handleEvaluateClick}>
                Evaluate
              </Button>
            </div>
          </Tippy>
        )}
      </Table.Cell>
    </Table.Row>
  );
};

export default CategoryRow;
